import ObexRequestsHandlerP80 from '../handlers/ObexRequestsHandlerP80';
import { PosTerminal } from '../types/PosTerminal.types';
import { getAccessToken, buildQuery, unwrapError, unwrapList, unwrapRecord, getTransportError } from './apiUtils';

const ROUTES = {
  list:     '/pos_terminals_list',
  register: '/terminal-token',
  update:   '/pos_terminals_update',
  remove:   '/pos_terminals_delete',
  token:    '/pos_terminals_token'
};

const STORAGE_KEY = 'albatross_pos_terminals';

/**
 * El backend solo expone terminal-token (alta o re-emisión de token por code,
 * con upsert sobre pos_terminals.code). No hay endpoint de listado, así que
 * las terminales dadas de alta desde este navegador se recuerdan en
 * localStorage. El token en claro solo se devuelve una vez y NUNCA se guarda.
 */
const listLocalTerminals = (): PosTerminal[] => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
};

const savePosTerminals = (terminals: PosTerminal[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(terminals));
  } catch {
    // sin almacenamiento: la terminal ya está en backend, solo se pierde el recordatorio local
  }
};

export const mapPosTerminal = (raw: any): PosTerminal => ({
  id:          String(raw.id),
  code:        raw.code || '',
  warehouseId: raw.warehouse_id !== undefined && raw.warehouse_id !== null ? String(raw.warehouse_id) : '',
  storeId:     raw.store_id !== undefined && raw.store_id !== null ? String(raw.store_id) : '',
  storeName:   raw.store_name || '',
  status:      raw.status === 'inactive' ? 'inactive' : 'active',
  createdAt:   raw.created_at || ''
});

/**
 * Listado desde pos_terminals_list. Si el endpoint aún no está publicado
 * (error de transporte) cae a las terminales recordadas en este navegador.
 */
export const listPosTerminals = async (): Promise<PosTerminal[]> => {
  const token = getAccessToken();
  if (!token) return listLocalTerminals();

  try {
    const res = await ObexRequestsHandlerP80.get(`${ROUTES.list}${buildQuery({ access_token: token })}`);
    if (getTransportError(res)) return listLocalTerminals();
    let rows = unwrapList(res);
    // Según cómo se publique el endpoint, el array llega anidado en result.result
    if (rows.length === 0) {
      const inner = res && res.data && res.data.result;
      const nested = inner && (Array.isArray(inner.result) ? inner.result : inner.result && inner.result.data);
      if (Array.isArray(nested)) rows = nested;
    }
    return rows.map(mapPosTerminal);
  } catch (err) {
    console.warn('pos_terminals_list no disponible, usando datos locales:', err);
    return listLocalTerminals();
  }
};

export const updatePosTerminal = async (
  id: string,
  storeId: string,
  status: PosTerminal['status']
): Promise<PosTerminal> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const query = buildQuery({ access_token: token, id, store_id: storeId, status });
  const res = await ObexRequestsHandlerP80.get(`${ROUTES.update}${query}`);
  const record = unwrapRecord(res);
  if (!record) throw new Error(unwrapError(res, 'Error al modificar la terminal.'));
  return mapPosTerminal(record);
};

export const deletePosTerminal = async (id: string): Promise<void> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const res = await ObexRequestsHandlerP80.get(`${ROUTES.remove}${buildQuery({ access_token: token, id })}`);
  if (!unwrapRecord(res)) throw new Error(unwrapError(res, 'Error al eliminar la terminal.'));
  savePosTerminals(listLocalTerminals().filter(t => t.id !== id));
};

/**
 * Token guardado de la terminal (pos_terminals_token), o null si no hay: terminales
 * creadas antes de guardarlo, o endpoint todavía no publicado.
 */
export const getPosTerminalToken = async (id: string): Promise<string | null> => {
  const token = getAccessToken();
  if (!token || !id) return null;
  try {
    const res = await ObexRequestsHandlerP80.get(`${ROUTES.token}${buildQuery({ access_token: token, id })}`);
    const record = unwrapRecord(res);
    return record && record.terminal_token ? String(record.terminal_token) : null;
  } catch (err) {
    console.warn('pos_terminals_token no disponible:', err);
    return null;
  }
};

/** Registra (o re-emite el token de) una terminal. Requiere rol admin en el backend. */
export const registerPosTerminal = async (
  code: string,
  storeId?: string
): Promise<{ terminal: PosTerminal; token: string }> => {
  const accessToken = getAccessToken();
  if (!accessToken) throw new Error('Sesión no válida o expirada.');

  const query = buildQuery({ access_token: accessToken, code, store_id: storeId });
  const res = await ObexRequestsHandlerP80.get(`${ROUTES.register}${query}`);

  const out = (res && res.data && res.data.result) || (res && res.data) || (res && res.result) || res;
  // terminal-token anida el resultado: {result: {status, terminal_token}}
  let payload = out && out.response !== undefined ? out.response : out;
  if (payload && typeof payload === 'object' && payload.result !== undefined && !payload.terminal_token) payload = payload.result;
  if (!payload || typeof payload !== 'object' || !payload.terminal_token) {
    // terminal-token no usa addResult con response: devuelve {result: "<mensaje>"}
    const msg = payload && typeof payload.result === 'string' ? payload.result : (typeof payload === 'string' ? payload : '');
    throw new Error(msg || unwrapError(res, 'Error al registrar la terminal.'));
  }

  const terminal: PosTerminal = {
    id: '',
    code,
    warehouseId: '',
    storeId: storeId || '',
    storeName: '',
    status: 'active',
    createdAt: new Date().toISOString()
  };
  const others = listLocalTerminals().filter(t => t.code !== code);
  savePosTerminals([terminal, ...others]);

  return { terminal, token: String(payload.terminal_token) };
};
